import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import {
  MatrizEmpresaResponse,
  MatrizSocioResponse,
  VisaoMatrizResponse,
} from '@interfaces/corporate-registry/responses/rede-visoes.response';
import { injectOwnershipNetworkDisplay } from '@shared/components/ownership-network/ownership-network-display';
import { CORES_GRAFO } from '@shared/components/ownership-network/registry-graph-geometry';
import {
  chaveCelula,
  corDaEmpresaMatriz,
  indexarLigacoes,
  textoOcultosMatriz,
} from '@shared/components/ownership-network/registry-matrix-index';

interface Celula {
  chave: string;
  ligado: boolean;
  cor: string;
  titulo: string | null;
  socioId: string;
  cnpj: string;
}

/**
 * Grade sócio × empresa já montada pela API: cada ligação = o sócio participa daquela empresa.
 * Aqui só se desenha — e se troca o eixo (Linhas: Empresas | Sócios).
 */
@Component({
  selector: 'app-registry-matrix',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './registry-matrix.component.html',
  styleUrl: './registry-matrix.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegistryMatrixComponent {
  readonly visao = input.required<VisaoMatrizResponse>();

  protected readonly cores = CORES_GRAFO;
  protected readonly display = injectOwnershipNetworkDisplay();
  protected readonly empresasNasLinhas = signal(true);
  protected readonly foco = signal<{ socio: string | null; empresa: string | null }>({
    socio: null,
    empresa: null,
  });

  protected readonly vazia = computed(
    () => !this.visao().empresas.length || !this.visao().socios.length
  );
  protected readonly ocultos = computed(() => textoOcultosMatriz(this.visao()));
  private readonly indice = computed(() => indexarLigacoes(this.visao().ligacoes));

  protected readonly corEmpresa = (e: MatrizEmpresaResponse) =>
    corDaEmpresaMatriz(e, this.display.highlightRoot());
  /** O `encurtar(nome, 26)` do `MatrizRede` do Dossiê — lá com "…", ao contrário do grafo. */
  protected readonly encurtar = (t: string) => (t.length > 26 ? `${t.slice(0, 25)}…` : t);

  /** Linhas já com as células resolvidas — o template não recalcula chave nem cor. */
  protected readonly linhas = computed(() => {
    const { empresas, socios } = this.visao();
    const { marcadas, qualificacao } = this.indice();
    const porEmpresa = this.empresasNasLinhas();
    const celula = (e: MatrizEmpresaResponse, s: MatrizSocioResponse): Celula => {
      const chave = chaveCelula(s.id, e.cnpj);
      const ligado = marcadas.has(chave);
      return {
        chave,
        ligado,
        cor: corDaEmpresaMatriz(e, this.display.highlightRoot()),
        titulo: ligado ? `${s.nome} — ${qualificacao.get(chave) ?? 'sócio'} — ${e.nome}` : null,
        socioId: s.id,
        cnpj: e.cnpj,
      };
    };
    return porEmpresa
      ? empresas.map(e => ({
          id: e.cnpj,
          empresa: e,
          socio: null,
          celulas: socios.map(s => celula(e, s)),
        }))
      : socios.map(s => ({
          id: s.id,
          empresa: null,
          socio: s,
          celulas: empresas.map(e => celula(e, s)),
        }));
  });

  protected readonly colunas = computed(() =>
    this.empresasNasLinhas()
      ? this.visao().socios.map(s => ({
          id: s.id,
          empresa: null as MatrizEmpresaResponse | null,
          socio: s as MatrizSocioResponse | null,
        }))
      : this.visao().empresas.map(e => ({
          id: e.cnpj,
          empresa: e as MatrizEmpresaResponse | null,
          socio: null as MatrizSocioResponse | null,
        }))
  );

  protected tituloEmpresa(e: MatrizEmpresaResponse): string {
    return `${e.nome}${e.situacao ? ` · ${e.situacao}` : ''}`;
  }

  protected tituloSocio(s: MatrizSocioResponse): string {
    return `${s.nome} (${s.tipo}) — liga ${s.grau} empresa(s) desta rede`;
  }

  protected emFoco(c: Celula): boolean {
    const f = this.foco();
    return f.socio === c.socioId || f.empresa === c.cnpj;
  }

  protected linhaEmFoco(id: string): boolean {
    const f = this.foco();
    return this.empresasNasLinhas() ? f.empresa === id : f.socio === id;
  }
}
